/**
 * ============================================================================
 * useKbExtranetAdmin — Hooks para curadoria da Extranet KB pelo admin do tenant
 * ----------------------------------------------------------------------------
 * Consomem as RPCs kb_admin_* (SECURITY DEFINER) criadas na Fase E:
 *   - kb_admin_list_external_users
 *   - kb_admin_get_external_user
 *   - kb_admin_set_external_user_status
 *   - kb_admin_grant_external_access
 *   - kb_admin_revoke_external_access
 *   - kb_admin_list_external_access_log
 *   - kb_admin_list_external_accesses
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.1.0 - Fase E3: adiciona `useKbCollectionsForGrant`
 *            (coleções por space) para o modal de concessão com escopo duplo.
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

export type KbExternalStatus = "pending" | "active" | "rejected" | "expired";
export type KbExternalAccessLevel = "read" | "comment";

export interface KbExternalUserRow {
  id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  organization: string | null;
  external_type: string;
  is_active: boolean;
  status: KbExternalStatus;
  expires_at: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  last_seen_at: string | null;
  approved_at: string | null;
  rejected_reason: string | null;
  access_count: number;
  last_login_at: string | null;
  created_at: string;
}

export interface KbExternalAccessRow {
  id: string;
  external_user_id: string;
  external_user_email: string | null;
  external_user_display_name: string | null;
  external_user_status: KbExternalStatus;
  space_id: string | null;
  space_name: string | null;
  collection_id: string | null;
  collection_name: string | null;
  access_level: KbExternalAccessLevel;
  expires_at: string | null;
  granted_at: string;
}

export interface KbExternalAccessLogRow {
  id: string;
  external_user_id: string | null;
  external_user_email: string | null;
  external_user_display_name: string | null;
  event_type: string;
  space_id: string | null;
  space_name: string | null;
  page_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

// ----------------------------------------------------------------------------
// Listagem de usuários externos (com filtros)
// ----------------------------------------------------------------------------
export function useKbExtranetUsers(params: { search?: string; status?: KbExternalStatus | "all" }) {
  const { search, status } = params;
  return useQuery({
    queryKey: ["kb-admin-extranet-users", search ?? "", status ?? "all"],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("kb_admin_list_external_users", {
        p_search: search && search.trim() ? search.trim() : null,
        p_status: status && status !== "all" ? status : null,
      });
      if (error) {
        logger.error("[useKbExtranetUsers] rpc failed", error);
        throw error;
      }
      return (data ?? []) as KbExternalUserRow[];
    },
    staleTime: 30_000,
  });
}

// ----------------------------------------------------------------------------
// Detalhe de um usuário externo (com acessos)
// ----------------------------------------------------------------------------
export function useKbExtranetUserDetails(externalUserId: string | null) {
  return useQuery({
    queryKey: ["kb-admin-extranet-user", externalUserId ?? ""],
    enabled: !!externalUserId,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("kb_admin_get_external_user", {
        p_external_user_id: externalUserId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return row ?? null;
    },
  });
}

// ----------------------------------------------------------------------------
// Mutação: alterar status (aprovar / rejeitar / desativar / expirar)
// ----------------------------------------------------------------------------
export function useKbExtranetSetStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { externalUserId: string; status: KbExternalStatus; reason?: string }) => {
      const { data, error } = await (supabase.rpc as any)("kb_admin_set_external_user_status", {
        p_external_user_id: input.externalUserId,
        p_status: input.status,
        p_reason: input.reason ?? null,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-users"] });
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-user"] });
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-accesses"] });
      toast({ title: "Status atualizado" });
    },
    onError: (e: any) => {
      toast({ title: "Falha ao atualizar status", description: e?.message, variant: "destructive" });
    },
  });
}

// ----------------------------------------------------------------------------
// Mutação: conceder acesso (space OU collection)
// ----------------------------------------------------------------------------
export function useKbExtranetGrantAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      externalUserId: string;
      spaceId?: string | null;
      collectionId?: string | null;
      accessLevel: KbExternalAccessLevel;
      expiresAt?: string | null;
    }) => {
      const { data, error } = await (supabase.rpc as any)("kb_admin_grant_external_access", {
        p_external_user_id: input.externalUserId,
        p_space_id: input.spaceId ?? null,
        p_collection_id: input.collectionId ?? null,
        p_access_level: input.accessLevel,
        p_expires_at: input.expiresAt ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-users"] });
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-accesses"] });
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-user"] });
      qc.invalidateQueries({ queryKey: ["extranet"] });
      toast({ title: "Acesso concedido" });
    },
    onError: (e: any) => {
      toast({ title: "Falha ao conceder acesso", description: e?.message, variant: "destructive" });
    },
  });
}

// ----------------------------------------------------------------------------
// Mutação: revogar acesso
// ----------------------------------------------------------------------------
export function useKbExtranetRevokeAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accessId: string) => {
      const { data, error } = await (supabase.rpc as any)("kb_admin_revoke_external_access", {
        p_access_id: accessId,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-users"] });
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-accesses"] });
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-user"] });
      qc.invalidateQueries({ queryKey: ["extranet"] });
      toast({ title: "Acesso revogado" });
    },
    onError: (e: any) => {
      toast({ title: "Falha ao revogar acesso", description: e?.message, variant: "destructive" });
    },
  });
}

// ----------------------------------------------------------------------------
// Listagem de acessos concedidos (visão pivô)
// ----------------------------------------------------------------------------
export function useKbExtranetAccesses() {
  return useQuery({
    queryKey: ["kb-admin-extranet-accesses"],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("kb_admin_list_external_accesses", {});
      if (error) throw error;
      return (data ?? []) as KbExternalAccessRow[];
    },
    staleTime: 30_000,
  });
}

// ----------------------------------------------------------------------------
// Auditoria (log de acesso)
// ----------------------------------------------------------------------------
export function useKbExtranetAccessLog(params: {
  externalUserId?: string | null;
  eventType?: string | null;
  limit?: number;
}) {
  const { externalUserId, eventType, limit } = params;
  return useQuery({
    queryKey: ["kb-admin-extranet-log", externalUserId ?? "", eventType ?? "", limit ?? 100],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("kb_admin_list_external_access_log", {
        p_external_user_id: externalUserId ?? null,
        p_event_type: eventType ?? null,
        p_limit: limit ?? 100,
      });
      if (error) throw error;
      return (data ?? []) as KbExternalAccessLogRow[];
    },
    staleTime: 15_000,
  });
}

// ----------------------------------------------------------------------------
// Helpers de spaces do tenant (para o modal de conceder acesso)
// Reaproveita a listagem crua de spaces do tenant via RPC pública já existente.
// ----------------------------------------------------------------------------
export function useKbSpacesForGrant() {
  return useQuery({
    queryKey: ["kb-admin-spaces-for-grant"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kb_spaces")
        .select("id, name, slug")
        .order("name");
      if (error) throw error;
      return (data ?? []) as { id: string; name: string; slug: string }[];
    },
    staleTime: 60_000,
  });
}

// ----------------------------------------------------------------------------
// Coleções de um space específico — usado no modal quando o escopo é coleção.
// ----------------------------------------------------------------------------
export function useKbCollectionsForGrant(spaceId: string | null) {
  return useQuery({
    queryKey: ["kb-admin-collections-for-grant", spaceId ?? ""],
    enabled: !!spaceId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kb_collections")
        .select("id, name, space_id")
        .eq("space_id", spaceId as string)
        .eq("is_archived", false)
        .order("name");
      if (error) throw error;
      return (data ?? []) as { id: string; name: string; space_id: string }[];
    },
    staleTime: 60_000,
  });
}
